/**
 * The three panels under a room: who is in focus, their session in tabs, and what their agent is
 * doing now.
 *
 * The Bug Hunt and the engineering floor show the same panels. The Bug Hunt adds its own things —
 * a mosquito for the ticket, its priority — through the slots below; the floor leaves them empty,
 * because it is not about bugs.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bot, CheckCircle2, Circle, CircleDot, Keyboard, Radio, XCircle } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n, type Locale } from '../lib/i18n'
import { formatCompact, formatCost, formatDuration, formatTime } from '../lib/format'
import { agentActivity } from './useSceneLabels'
import { Portrait, lookFor } from './Figures'
import { ProgressBar, Segmented, Skeleton } from '../components/primitives'
import { TeamChips } from '../components/TeamChips'
import { testsFrom, type PanelSession } from './panelSession'
import { promptSegments, type PromptSegment } from '../lib/promptSegments'
import { preview, promptFor, readablePrompts } from '../lib/promptPreview'
import { filesTouched } from './filesTouched'
import { folderFor } from '../lib/repoFolders'
import type { AgentReply, ActivityState, Avatar, SessionPrompt, TeamRef, TimelineEntry, WorkSessionDetail, WorkStep } from '../lib/types'

/* ------------------------------------------------------------------- focus */

export function DeveloperFocus({
  person,
  working,
  session,
  detail,
  lead,
  tag,
}: {
  person: { id: string; name: string; avatar: Avatar | null; teams: TeamRef[] }
  /** An agent of theirs is running now. */
  working: boolean
  session: PanelSession | undefined
  detail: WorkSessionDetail | undefined
  /** Drawn before the ticket: the Bug Hunt's mosquito, the floor's kind of work. */
  lead?: ReactNode
  /** Drawn after the ticket: the Bug Hunt's priority. */
  tag?: ReactNode
}) {
  const { t, locale } = useI18n()
  const look = useMemo(() => lookFor(person.id, person.avatar), [person.id, person.avatar])
  const tests = session ? testsFrom(session, detail) : null

  return (
    <div className="stack stack--tight" style={{ minHeight: 0 }}>
      <div className="row row--tight">
        <Portrait look={look} size={44} />
        <div className="stack" style={{ gap: 0, minWidth: 0 }}>
          <span className="bold truncate">
            {person.name} <span className={`bh-status-dot${working ? ' bh-status-dot--on' : ''}`} />
          </span>
          {person.teams.length > 0 && <TeamChips teams={person.teams} />}
          <Link to={`/developers/${person.id}/day`} className="tiny">{t('day.open')}</Link>
          <span className="tiny" style={{ color: statusColor(working ? session?.activityState : undefined) }}>
            {working && session
              ? session.hasProgress
                ? `${agentActivity(session.activityState, t)} · ${Math.round(session.progressPercent)}%`
                : agentActivity(session.activityState, t)
              : t('activity.noAgent')}
          </span>
        </div>
      </div>

      {!session ? (
        <span className="tiny muted">{t('bugHunt.noActiveSession')}</span>
      ) : (
        <>
          <Link to={`/work/${session.workSessionId}`} className="bh-focus__task">
            <div className="row row--tight">
              {lead}
              <span className="mono small bold">{session.reference ?? t('activity.exploration')}</span>
              {tag}
              <div className="spacer" />
              {session.hasProgress && <span className="tiny bold">{Math.round(session.progressPercent)}%</span>}
            </div>
            <span className="small truncate">{session.title ?? session.currentStepTitle ?? '—'}</span>
            {session.hasProgress && <ProgressBar value={session.progressPercent} size="sm" />}
          </Link>

          <div className="row row--between tiny muted">
            <span className="truncate">{t('bugHunt.ai')}: {session.aiName ?? '—'}</span>
            <span className="nowrap">{t('bugHunt.started')} {formatTime(session.startedAt, locale)}</span>
            <span className="nowrap">{t('bugHunt.elapsed')} {formatDuration(session.durationSeconds, locale)}</span>
          </div>

          <div className="bh-stats">
            <Stat label={t('bugHunt.stat.files')} value={detail ? String(detail.codeChanges.filesChanged) : '—'} />
            <Stat
              label={t('bugHunt.stat.lines')}
              value={
                detail ? (
                  <>
                    <span style={{ color: 'var(--ok-text)' }}>+{detail.codeChanges.linesAdded}</span>
                    <span className="bh-stat__minor" style={{ color: 'var(--danger-text)' }}>−{detail.codeChanges.linesDeleted}</span>
                  </>
                ) : '—'
              }
            />
            <Stat label={t('bugHunt.stat.tests')} value={tests ? `${tests.passed}/${tests.total}` : '—'} />
            <Stat
              label={t('bugHunt.stat.tokens')}
              value={detail ? formatTokens(detail.aiUsage.inputTokens + detail.aiUsage.outputTokens, locale) : '—'}
            />
            {/* Never a cost without saying how sure it is: the accuracy is the tile's tooltip, and
                an unavailable cost is a dash rather than a zero. */}
            <Stat
              label={t('bugHunt.stat.cost')}
              title={detail ? t(`cost.${detail.aiUsage.accuracy}`) : undefined}
              value={
                !detail || detail.aiUsage.accuracy === 'Unavailable' || detail.aiUsage.costUsd == null
                  ? '—'
                  : formatCost(detail.aiUsage.costUsd, locale)
              }
            />
          </div>
        </>
      )}
    </div>
  )
}

function Stat({ label, value, title }: { label: string; value: ReactNode; title?: string }) {
  return (
    <div className="bh-stat" title={title}>
      <span className="bh-stat__label">{label}</span>
      <span className="bh-stat__value">{value}</span>
    </div>
  )
}

/* -------------------------------------------------------------------- tabs */

/** The tabs, in the order §26 of the brief lists them — and then the prompt that started it all. */
const TABS = [
  { value: 'log', key: 'bugHunt.tab.log' },
  { value: 'plan', key: 'bugHunt.tab.plan' },
  { value: 'files', key: 'bugHunt.tab.files' },
  { value: 'tests', key: 'bugHunt.tab.tests' },
  { value: 'usage', key: 'bugHunt.tab.usage' },
  { value: 'timeline', key: 'bugHunt.tab.timeline' },
  { value: 'prompt', key: 'bugHunt.tab.prompt' },
] as const

/**
 * What the session in focus is doing, in tabs.
 *
 * §26 asks for Live Log, Plan, Files, Tests, AI Usage and Timeline, and to reuse existing data
 * rather than build parallel endpoints. The log comes from the room's live stream when it has one;
 * everything else is the session detail the /work page uses. A manager who wants the full picture
 * still follows the link to the session page — this is the glance, not the study.
 */
export function SessionTabs({
  session,
  detail,
  log,
}: {
  session: PanelSession | undefined
  detail: WorkSessionDetail | undefined
  log: Array<{ id: number; at: string; text: string }>
}) {
  const { t, locale } = useI18n()
  const [tab, setTab] = useState<(typeof TABS)[number]['value']>('log')

  /*
    Two ways to read a session. By default it is the whole thing, which is what somebody watching
    the room wants. "By prompt" cuts it at each prompt and shows one stretch at a time — what
    happened after that one ask — because that is the question people have when they come back to a
    session they set off themselves.
  */
  const [view, setView] = useState<'all' | 'prompt'>('all')
  const [picked, setPicked] = useState<string | null>(null)

  const segments = useMemo(() => promptSegments(detail?.timeline ?? []), [detail])
  const segment = view === 'prompt' ? segments.find((s) => s.id === picked) ?? segments[0] : undefined

  /*
    The prompts, for the log and the timeline to show what was asked instead of how long it was.

    Same query key as the Prompt tab, so the two share one request and one cache. The server decides
    what comes back: nothing at all for a reader who may not see prompts, which is exactly the case
    the fallback below covers.
  */
  const { data: promptData } = useQuery({
    queryKey: ['work', session?.workSessionId, 'prompts'],
    queryFn: () => api.sessions.prompts(session!.workSessionId),
    enabled: Boolean(session),
    refetchInterval: 30_000,
  })

  const prompts = useMemo(() => readablePrompts(promptData), [promptData])

  /**
   * What a line in the log says.
   *
   * A prompt reads as its opening words when the reader may read it, and as its length when they
   * may not — which is what the server wrote into the timeline in the first place, and stays true
   * whatever the organization has turned on.
   */
  const describe = (at: string, fallback: string | null, entry?: TimelineEntry): string => {
    const match = promptFor(prompts, at)
    if (match?.text) return preview(match.text)

    if (entry?.eventType === 'prompt.submitted') {
      const chars = typeof entry.payload?.chars === 'number' ? entry.payload.chars : 0
      if (chars > 0) return t('session.log.promptSent', { count: chars })
    }

    return fallback ?? ''
  }

  // What the tabs read: everything, or only the chosen stretch.
  const timeline = segment ? segment.events : detail?.timeline ?? []

  // Runs that finished inside the stretch, matched by when they finished.
  const testRuns = useMemo(() => {
    if (!detail) return []
    if (!segment) return detail.testRuns

    const window = new Set(segment.events.filter((e) => e.eventType === 'test.completed').map((e) => e.occurredAt.slice(0, 19)))
    return detail.testRuns.filter((run) => run.completedAt && window.has(run.completedAt.slice(0, 19)))
  }, [detail, segment])

  // The live log only holds what arrived since the page opened. Until something does, it shows
  // what the session already reported, newest first, so opening the page is not an empty panel.
  // A chosen stretch is always read from the session's own events: the live log has no windows.
  const lines =
    log.length > 0 && !segment
      ? log.slice(0, 30).map((entry) => ({ id: String(entry.id), at: entry.at, text: describe(entry.at, entry.text) }))
      : timeline
          .slice(-30)
          .reverse()
          .map((entry) => ({ id: entry.eventId, at: entry.occurredAt, text: describe(entry.occurredAt, entry.summary, entry) }))

  return (
    <>
      <div className="bughunt__tabs" role="tablist">
        {TABS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={tab === option.value}
            className={`bughunt__tab${tab === option.value ? ' bughunt__tab--active' : ''}`}
            onClick={() => setTab(option.value)}
            disabled={option.value !== 'log' && !session}
          >
            {t(option.key)}
          </button>
        ))}

        <div className="spacer" />

        {session && (
          <Segmented<'all' | 'prompt'>
            value={view}
            options={[
              { value: 'all', label: t('session.view.all') },
              { value: 'prompt', label: t('session.view.byPrompt') },
            ]}
            onChange={setView}
          />
        )}
      </div>

      {view === 'prompt' && (
        <PromptFilter
          segments={segments}
          chosen={segment}
          onPick={setPicked}
        />
      )}

      <div className="bughunt__tab-body">
        {tab === 'log' &&
          (lines.length === 0 ? (
            <span className="tiny muted">{t('common.noResults')}</span>
          ) : (
            lines.map((entry) => (
              <div key={entry.id} className="bughunt__log-line">
                <span className="mono muted">{formatTime(entry.at, locale)}</span>
                <CircleDot size={11} className="bughunt__log-icon" />
                <span className="truncate">{entry.text}</span>
              </div>
            ))
          ))}

        {tab !== 'log' && tab !== 'prompt' && session && !detail && <Skeleton height={90} />}

        {tab === 'plan' && detail && <PlanList steps={detail.plan?.steps ?? []} />}

        {tab === 'files' && detail && (
          <div className="stack stack--tight tiny">
            <div className="row row--between">
              <span className="muted">{t('work.filesChanged')}</span>
              <span className="nowrap">
                <span className="mono">{segment ? segment.metrics.filesChanged : detail.codeChanges.filesChanged}</span>{' '}
                <span style={{ color: 'var(--ok-text)' }}>+{segment ? segment.metrics.linesAdded : detail.codeChanges.linesAdded}</span>{' '}
                <span style={{ color: 'var(--danger-text)' }}>−{segment ? segment.metrics.linesDeleted : detail.codeChanges.linesDeleted}</span>
              </span>
            </div>

            {/* Whether these counts are the agent's estimate or what git actually says. */}
            {!segment && <span className="muted">{t(`work.metricSource.${detail.codeChanges.source}`)}</span>}

            {/* Each file by name, with where it is: the full path on this computer when the project's folder is known
                (the desktop), else its path in the project. A path, never contents: the protocol has no field for them. */}
            {(() => {
              const repository = detail.session.repositoryName
              const files = filesTouched(segment ? segment.metrics.files : detail.codeChanges.filePaths, timeline, repository ? folderFor(repository) : null)
              if (files.length === 0) return <span className="muted">{t('common.noResults')}</span>
              return files.slice(0, 30).map((file) => (
                <div key={file.path} className="stack session-file" title={file.local ?? file.path}>
                  <span className="row row--tight">
                    <strong className="mono truncate">{file.name}</strong>
                    {file.byAgent && <span className="tiny session-file__by">{t('work.fileByAgent')}</span>}
                  </span>
                  <span className="mono truncate muted session-file__path">{file.local ?? file.path}</span>
                </div>
              ))
            })()}
          </div>
        )}

        {tab === 'tests' && detail && (
          <div className="stack stack--tight">
            {testRuns.length === 0 ? (
              <span className="tiny muted">{t('common.noResults')}</span>
            ) : (
              testRuns.slice(0, 10).map((run) => (
                <div key={run.id} className="row row--between tiny">
                  <span className="truncate">{run.suite ?? run.framework ?? '—'}</span>
                  <span style={{ color: run.failed > 0 ? 'var(--danger-text)' : 'var(--ok-text)' }}>
                    {run.passed}/{run.total}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'usage' && detail && segment && (
          <div className="stack stack--tight tiny">
            <div className="row row--between">
              <span className="muted">{t('bugHunt.stat.tokens')}</span>
              <span className="mono">{formatCompact(segment.metrics.tokens, locale)}</span>
            </div>
            <div className="row row--between">
              <span className="muted">{t('live.cost')}</span>
              <span className="mono">
                {segment.metrics.costUsd == null ? '—' : formatCost(segment.metrics.costUsd, locale)}
              </span>
            </div>
            {/* This stretch's own figures, summed from what the agent reported inside it. */}
            <span className="muted">{t('session.view.inThisPrompt')}</span>
          </div>
        )}

        {tab === 'usage' && detail && !segment && (
          <div className="stack stack--tight tiny">
            <div className="row row--between">
              <span className="muted">{t('work.inputTokens')}</span>
              <span className="mono">{formatCompact(detail.aiUsage.inputTokens, locale)}</span>
            </div>
            <div className="row row--between">
              <span className="muted">{t('work.outputTokens')}</span>
              <span className="mono">{formatCompact(detail.aiUsage.outputTokens, locale)}</span>
            </div>
            {detail.aiUsage.cachedInputTokens > 0 && (
              <div className="row row--between">
                <span className="muted">{t('work.cachedTokens')}</span>
                <span className="mono">{formatCompact(detail.aiUsage.cachedInputTokens, locale)}</span>
              </div>
            )}
            <div className="row row--between">
              <span className="muted">{t('live.cost')}</span>
              <span className="mono">
                {detail.aiUsage.accuracy === 'Unavailable' || detail.aiUsage.costUsd == null
                  ? '—'
                  : formatCost(detail.aiUsage.costUsd, locale)}
              </span>
            </div>
            {/* Cost is never shown without saying how sure it is. */}
            <span className="muted">{t(`cost.${detail.aiUsage.accuracy}`)}</span>
            {detail.aiUsage.modelName && <span className="muted">{detail.aiUsage.modelName}</span>}
          </div>
        )}

        {tab === 'timeline' &&
          detail &&
          timeline.slice(-20).reverse().map((entry) => (
            <div key={entry.eventId} className="bughunt__log-line">
              <span className="mono muted">{formatTime(entry.occurredAt, locale)}</span>
              <span className="truncate">{describe(entry.occurredAt, entry.summary, entry)}</span>
            </div>
          ))}

        {tab === 'prompt' && session && <PromptTab workSessionId={session.workSessionId} />}
      </div>
    </>
  )
}

/**
 * The prompts of a session, as a filter: pick one and the tabs show only its stretch.
 *
 * Each chip says when it was sent and how long it was; the line under them is what that stretch
 * cost — time, files, tests, tokens — summed from the events inside it and from nothing else.
 */
function PromptFilter({
  segments,
  chosen,
  onPick,
}: {
  segments: PromptSegment[]
  chosen: PromptSegment | undefined
  onPick: (id: string) => void
}) {
  const { t, locale } = useI18n()

  if (segments.length === 0) return <span className="tiny muted bh-prompt-filter">{t('session.view.noPrompts')}</span>

  return (
    <div className="bh-prompt-filter">
      <div className="bh-prompt-filter__chips" role="tablist" aria-label={t('session.view.byPrompt')}>
        {segments.map((segment) => (
          <button
            key={segment.id}
            type="button"
            role="tab"
            aria-selected={segment.id === chosen?.id}
            className={`bh-prompt-chip${segment.id === chosen?.id ? ' bh-prompt-chip--on' : ''}`}
            onClick={() => onPick(segment.id)}
          >
            <span className="mono">{formatTime(segment.at, locale)}</span>
            <span className="tiny muted">
              {segment.id === 'start' ? t('session.view.beforePrompts') : t('session.view.chars', { count: segment.chars ?? 0 })}
            </span>
          </button>
        ))}
      </div>

      {chosen && (
        <div className="row row--tight row--wrap tiny muted bh-prompt-filter__metrics">
          <span>{formatDuration(chosen.metrics.seconds, locale)}</span>
          <span>{t('bugHunt.stat.files')}: {chosen.metrics.filesChanged}</span>
          <span style={{ color: 'var(--ok-text)' }}>+{chosen.metrics.linesAdded}</span>
          <span style={{ color: 'var(--danger-text)' }}>−{chosen.metrics.linesDeleted}</span>
          {chosen.metrics.testRuns > 0 && <span>{t('bugHunt.stat.tests')}: {chosen.metrics.testsPassed}/{chosen.metrics.testsTotal}</span>}
          {chosen.metrics.tokens > 0 && <span>{t('bugHunt.stat.tokens')}: {formatCompact(chosen.metrics.tokens, locale)}</span>}
          {chosen.metrics.costUsd != null && <span>{formatCost(chosen.metrics.costUsd, locale)}</span>}
          {chosen.metrics.stepsCompleted > 0 && <span>{t('session.view.steps', { count: chosen.metrics.stepsCompleted })}</span>}
        </div>
      )}
    </div>
  )
}

/**
 * The prompt that started the session, and those sent while it ran.
 *
 * Fetched only when the tab is opened. The server decides what the reader may see: the text only
 * for the developer and their managers, and only when the organization keeps prompts — anybody
 * else is told why there is nothing, rather than shown an empty box.
 */
export function PromptTab({ workSessionId }: { workSessionId: string }) {
  const { t, locale } = useI18n()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['work', workSessionId, 'prompts'],
    queryFn: () => api.sessions.prompts(workSessionId),
    refetchInterval: 30_000,
  })

  if (isLoading) return <Skeleton height={90} />
  if (isError || !data) return <span className="tiny muted">{t('error.generic')}</span>

  if (data.access === 'Restricted') {
    return <span className="tiny muted">{t('bugHunt.prompt.restricted', { count: data.promptCount })}</span>
  }

  const nothing = !data.trigger && data.followUps.length === 0

  return (
    <div className="stack stack--tight bh-prompts">
      {data.access === 'NotKept' && <span className="tiny muted">{t('bugHunt.prompt.notKept', { count: data.promptCount })}</span>}
      {nothing && data.access === 'Kept' && data.replies.length === 0 && <span className="tiny muted">{t('bugHunt.prompt.none')}</span>}
      {/* Answers with no prompt to hang them on (the prompts are not kept, the person's own answers
          are): shown on their own, in order. */}
      {nothing && data.replies.map((reply) => <ReplyBlock key={reply.id} reply={reply} locale={locale} />)}

      {data.trigger && <PromptBlock prompt={data.trigger} label={t('bugHunt.prompt.trigger')} locale={locale} />}
      {repliesTo(data.trigger?.submittedAt, data.followUps.at(0)?.submittedAt, data.replies).map((reply) => (
        <ReplyBlock key={reply.id} reply={reply} locale={locale} />
      ))}

      {data.followUps.length > 0 && (
        <>
          <span className="tiny muted">{t('bugHunt.prompt.followUps', { count: data.followUps.length })}</span>
          {data.followUps.map((prompt, index) => (
            <Fragment key={prompt.id}>
              <PromptBlock prompt={prompt} locale={locale} />
              {/* What the agent answered before the next thing was asked. */}
              {repliesTo(prompt.submittedAt, data.followUps.at(index + 1)?.submittedAt, data.replies).map((reply) => (
                <ReplyBlock key={reply.id} reply={reply} locale={locale} />
              ))}
            </Fragment>
          ))}
        </>
      )}
    </div>
  )
}

/**
 * The replies that came between one prompt and the next.
 *
 * A reply belongs to whatever was asked before it, which is all the ordering anybody needs: the tab
 * then reads as the conversation it was.
 */
function repliesTo(from: string | undefined, until: string | undefined, replies: AgentReply[]): AgentReply[] {
  if (!from) return []
  const after = Date.parse(from)
  const before = until ? Date.parse(until) : Number.POSITIVE_INFINITY

  return replies.filter((reply) => {
    const at = Date.parse(reply.repliedAt)
    return at >= after && at < before
  })
}

/**
 * What the agent answered — shown only when the organization keeps agent replies, which is off
 * until an administrator turns it on.
 */
function ReplyBlock({ reply, locale }: { reply: AgentReply; locale: Locale }) {
  const { t } = useI18n()

  return (
    <div className="bh-prompt bh-prompt--reply">
      <div className="row row--tight row--wrap tiny muted">
        <Bot size={12} aria-hidden />
        <span>{t('bugHunt.prompt.reply')}</span>
        <div className="spacer" />
        <span className="mono">{formatTime(reply.repliedAt, locale)}</span>
      </div>
      <p className="small bh-prompt__text">{reply.text}</p>
    </div>
  )
}

/**
 * One prompt, always saying where it came from: typed into the agent, or sent from the
 * remote-control page. A remote command read by anybody but its developer comes without its text.
 */
function PromptBlock({ prompt, label, locale }: { prompt: SessionPrompt; label?: string; locale: Locale }) {
  const { t } = useI18n()
  const remote = prompt.source === 'Remote'

  return (
    <div className={`bh-prompt${remote ? ' bh-prompt--remote' : ''}`}>
      <div className="row row--tight row--wrap tiny muted">
        {label && <span className="bold">{label}</span>}
        <span className="mono">{formatTime(prompt.submittedAt, locale)}</span>
        <span className={`bh-prompt__source${remote ? ' bh-prompt__source--remote' : ''}`}>
          {remote ? <Radio size={11} aria-hidden /> : <Keyboard size={11} aria-hidden />}
          {remote ? t('bugHunt.prompt.fromRemote') : t('bugHunt.prompt.fromEditor')}
        </span>
      </div>
      {/* Already redacted by the server; shown as written, never as markup. */}
      {prompt.text ? (
        <p className="bh-prompt__text">{prompt.text}</p>
      ) : (
        <p className="bh-prompt__text bh-prompt__text--hidden">{t('bugHunt.prompt.remoteHidden')}</p>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------- agent */

/**
 * What the agent in focus is doing now, and its plan as a checklist.
 *
 * The first line is the agent's own latest note, verbatim; the checklist is its published plan
 * with each step's reported status. Nothing here is composed by the screen.
 */
export function AgentCard({
  session,
  detail,
  decoration,
}: {
  session: PanelSession | undefined
  detail: WorkSessionDetail | undefined
  /** Drawn in the corner: the Bug Hunt's mosquito. */
  decoration?: ReactNode
}) {
  const { t } = useI18n()
  const running = Boolean(session) && session!.activityState !== 'Idle'

  return (
    <div className={`bh-panel bh-agentcard${decoration ? ' bh-agentcard--decorated' : ''}`}>
      <div className="row row--tight">
        <span className={`bh-agentcard__icon${running ? ' bh-agentcard__icon--on' : ''}`}>
          <Bot size={16} />
        </span>
        <div className="stack" style={{ gap: 0, minWidth: 0 }}>
          <span className="bold small">{running ? t('bugHunt.agentWorking') : t('activity.noAgent')}</span>
          {session && (
            <span className="tiny muted bh-agentcard__note">
              {session.currentStepNote ?? session.currentStepTitle ?? t('activity.noSubject')}
            </span>
          )}
        </div>
      </div>

      {!session ? (
        <span className="tiny muted">{t('bugHunt.noActiveSession')}</span>
      ) : !detail ? (
        <Skeleton height={80} />
      ) : (
        <PlanList steps={detail.plan?.steps ?? []} />
      )}

      {decoration}
    </div>
  )
}

export function PlanList({ steps }: { steps: WorkStep[] }) {
  const { t } = useI18n()

  if (steps.length === 0) return <span className="tiny muted">{t('bugHunt.noPlan')}</span>

  return (
    <ul className="bh-checklist">
      {steps.map((step) => (
        <li key={step.id} className={`bh-checklist__item bh-checklist__item--${step.status}`}>
          {step.status === 'Completed' ? (
            <CheckCircle2 size={14} />
          ) : step.status === 'InProgress' ? (
            <CircleDot size={14} />
          ) : step.status === 'Failed' ? (
            <XCircle size={14} />
          ) : (
            <Circle size={14} />
          )}
          <span className="truncate">{step.title}</span>
        </li>
      ))}
    </ul>
  )
}

/* ----------------------------------------------------------------- helpers */

/** The colour of an agent's state under a name: alarm for blocked, calm for running, grey for none. */
function statusColor(state: ActivityState | undefined): string {
  if (!state || state === 'Idle') return 'var(--text-muted)'
  if (state === 'Blocked') return 'var(--danger-text)'
  return 'var(--ok-text)'
}

/** Tokens in three significant digits — "161 mil", "12,3 mil" — which is all a tile has room for. */
function formatTokens(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumSignificantDigits: 3 }).format(value)
}
